import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import {
  CROP_FIELD_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
} from '../../../../router/routes';
import Field from '../../../../interfaces/field';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../../components/common/tabs/tab-container';
import ViewAnalyses from './analyses/view';
import ViewBasicInfo from './basic-info/view';
import ViewWorks from './works/view';
import ActionButtons from './components/action-buttons';
import css from './view.module.css';
import TabsKeys from '../../../../enums/tab-keys';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import indexCss from '../index.module.css';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { LinkComponent } from '../../../../components/form/elements/link';
import { Section } from '../../../../pages/crop/fields-and-works';

export default function ViewField() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, farmId, season, tab } = useParams<{
    id: string;
    farmId: string;
    season: string;
    tab: string;
  }>();
  const { fieldService, workService } = useService();
  const [field, setField] = useState(null);
  const [title, setTitle] = useState(null);
  const [currentTab, setTab] = useState(tab);

  useEffect(() => {
    const routeTo = route(CROP_FIELD_VIEW, {
      id: id,
      tab: currentTab,
      farmId: farmId,
      season: season,
    });

    if (routeTo !== history.location.pathname) {
      history.push(routeTo);
    }
  }, [history, farmId, season, id, currentTab]);

  useEffect(() => {
    const subscription = fieldService
      .show(farmId, id)
      .subscribe((field: Field) => {
        field.map_loaded = true;
        field.initial_geometry = field.geometry;
        field.initial_area = field.area;
        field.initial_waypoints = field.waypoints ?? [];
        field.ownership_id = {
          value: field.ownership_id as string,
          label: field.ownership_value,
        };

        setField(field);
        setTitle(field.label);
      });

    return () => subscription.unsubscribe();
  }, [fieldService, id, farmId, currentTab]);

  return (
    <div className={`${indexCss['container']} ${indexCss['show-map']}`}>
      <div className={css['field-info']}>
        <div className={css['header']}>
          <LinkComponent
            label="back"
            icon={faLongArrowLeft}
            linkTo={route(CROP_FIELDS_AND_WORKS_INDEX, {
              farmId: farmId,
              season: season,
              tab: Section.fields,
            })}
          />
          <h2>{title ? title : ''}</h2>
        </div>
        {field && field.active && (
          <div className={css['toolbar']}>
            <ShowIfAllowed ability={{ title: 'create work' }}>
              <ActionButtons
                enabledButtons={workService.allowedWorkTypesForFields([field])}
                fields={[id]}
              />
            </ShowIfAllowed>
          </div>
        )}
        <div className={css['content']}>
          <TabContainer
            selectedTabKey={currentTab}
            setTab={setTab}
            tabNavigation={
              <TabNavigation theme={'pages'}>
                <TabLink tabKey={TabsKeys.basic_info} theme={'pages'}>
                  {/* FIXME Style prop value must be an object */}
                  {t('pages.crop.fields.basic-info')}
                </TabLink>
                <TabLink tabKey={TabsKeys.analyses} theme={'pages'}>
                  {/* FIXME Style prop value must be an object */}
                  {t('pages.crop.fields.analyses')}
                </TabLink>
                <ShowIfAllowed ability={{ title: 'viewAny work' }}>
                  <TabLink tabKey={TabsKeys.works} theme={'pages'}>
                    {/* FIXME Style prop value must be an object */}
                    {t('pages.crop.fields.works')}
                  </TabLink>
                </ShowIfAllowed>
              </TabNavigation>
            }
            tabContents={
              <TabContents theme={'pages'}>
                <Tab tabKey={TabsKeys.basic_info}>
                  <ViewBasicInfo field={field} />
                </Tab>
                <Tab tabKey={TabsKeys.analyses}>
                  <ViewAnalyses />
                </Tab>
                <ShowIfAllowed ability={{ title: 'viewAny work' }}>
                  <Tab tabKey={TabsKeys.works}>
                    <ViewWorks />
                  </Tab>
                </ShowIfAllowed>
              </TabContents>
            }
          />
        </div>
      </div>
    </div>
  );
}
